/** The console's View and Layers tabs: how units are coloured and faded,
 *  which sides show, whose sight (fog of war), the colour key, and the map
 *  layers. They edit the renderer's options; `rev` re-reads them after any
 *  change (keys change them too). */

import { For, Show, createSignal, type Accessor, type JSX } from "solid-js";
import { render } from "solid-js/web";
import type { Header } from "../replay/format";
import { MARK_COLOURS, MARK_LABELS, ORE_UNKNOWN, SAT_LABELS, SAT_LOW, TINT, TYPE_TINTS, mixRGB, oreRGB, ORE_FAMILIES, ORE_HUES, oreLabel, oresOf, type RGB } from "../render/colour";
import type { RenderOptions } from "../render/renderer";
import * as keys from "../ui/bindings";

/** A layer: its switch, and what it shows and how to read it (its tooltip,
 *  from what the renderer draws). */
export type Toggle = { key: keyof RenderOptions; label: string; tip: string };

export const TOGGLES: Toggle[] = [
  { key: "units", label: "Units", tip: "Every unit, in its side's colour. Zoomed out, armies shade the ground instead." },
  { key: "fire", label: "Fire", tip: "A tracer from each unit to what it's shooting at, in the shooter's colour." },
  { key: "shells", label: "Shells", tip: "Artillery in flight: a faint red dashed line from gun to where it lands." },
  { key: "trains", label: "Trains", tip: "Trains on the rail: in their side's colour carrying troops, rust carrying freight, grey carrying rounds." },
  { key: "track", label: "Rail", tip: "Track: a brown line with sleepers through every rail hex." },
  { key: "depots", label: "Depots", tip: "Structures: home, depots, stations, mines, plants and barracks, each its own glyph." },
  { key: "corridors", label: "Corridors", tip: "Supply corridors: the roads convoys take from depots to the front, shaded straw. Fighting units keep off them." },
  { key: "effects", label: "Effects", tip: "Shell bursts, and a red cross where a unit died." },
  { key: "zones", label: "Zones", tip: "The world's zones (how it's shared between servers), as blue-grey borders." },
  { key: "grid", label: "Hex grid", tip: "Hex edges, up close." },
  { key: "minimap", label: "Minimap", tip: "The whole map in the console, structures marked; click to look, right-click to order." },
  { key: "territory", label: "Territory", tip: "Ground settled, washed in its settler's colour: deeper is more settled." },
  { key: "intel", label: "Ore found", tip: "What prospecting found: each ore in its colour, brighter and more solid where richer." },
  { key: "flows", label: "Goods flow", tip: "Freight on its way between your stations: an arrow a good along the rail, thicker the more." },
  { key: "fullWarnings", label: "Station full", tip: "A tag on each structure whose station has no room for what it makes. Its card says so either way." },
  { key: "samples", label: "Sample timers", tip: "Each prospecting sample under way: a ring filling and the time left. Shown anyway while prospectors are selected." },
];

/** Keys for the route-like layers, shown while they're on: a swatch (a
 *  CSS background) and what it means. */
const KEYS: Partial<Record<keyof RenderOptions, [string, string][]>> = {
  track: [["#9e8061", "rail"]],
  trains: [
    ["var(--accent)", "troops (side's colour)"],
    ["#b86b3d", "freight"],
    ["#8c8c8c", "rounds"],
  ],
  corridors: [["rgba(191, 173, 115, 0.5)", "supply corridor"]],
  flows: [
    ["#d0603a", "iron ore"],
    ["#9aa3ad", "steel"],
    ["#ece2c4", "rounds"],
  ],
};

/** Whether the commander has prospected anything (the ore heatmap's
 *  choices and key show then). */
const [oreKnown, setOreKnown] = createSignal(false);
/** The ores found so far (sim names), for the on-map key. */
const [oresFound, setOresFound] = createSignal<string[]>([]);
export { setOresFound };
/** Whether the Ore found layer is on (the on-map key shows with it). */
const [oreLayer, setOreLayer] = createSignal(true);
export { setOreKnown, setOreLayer };

/** The heatmap's colours, on the map itself while it shows something
 *  found: a newcomer can't read glows without knowing which is which. */
function OreKey(): JSX.Element {
  return (
    <Show when={oreKnown() && oreLayer()}>
      <div class="map-ore-key" aria-label="Ore colours">
        <For each={ORE_HUES.map(([o]) => o).filter((o) => oresFound().includes(o) && oresOf(oreLayerChoice()).includes(o))}>
          {(ore) => (
            <span>
              <span class="dot" style={{ background: css(oreRGB(ore)) }} />
              {oreLabel(ore)}
            </span>
          )}
        </For>
      </div>
    </Show>
  );
}


/** The heatmap's choice (All, a family, an ore), for the on-map key. */
const [oreLayerChoice, setOreLayerChoice] = createSignal("All");
export { setOreLayerChoice };

const css = (c: RGB) => `rgb(${c.map((v) => Math.round(v * 255)).join(",")})`;

interface Props {
  header: Header;
  options: RenderOptions;
  /** Bumped on every change: what the tabs show is re-read from `options`. */
  rev: Accessor<number>;
  /** Applies a change to `options` and redraws. */
  change: (f: (o: RenderOptions) => void) => void;
}

export function ViewTab(p: Props): JSX.Element {
  const o = () => (p.rev(), p.options);
  const teams = p.header.factions.map((f) => {
    const n = parseInt(f.color.slice(1), 16);
    return [(n >> 16) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255] as RGB;
  });
  // Swatches show each tint applied to every team's colour.
  const item = (hue: RGB | null, label: string) => (
    <span class="legend-item">
      <span class="swatches">
        <For each={teams}>{(t) => <span class="swatch" style={{ background: css(hue ? mixRGB(t, hue, TINT) : t) }} />}</For>
      </span>
      {label}
    </span>
  );
  const sides: [number, string][] = [[-1, "Everything"], ...p.header.factions.map((f, i) => [i, f.name] as [number, string])];
  return (
    <>
      <div class="view-row">
        <span class="row-label">Tint</span>
        <div class="options" role="radiogroup" aria-label="Tint units by">
          <For each={MARK_LABELS}>
            {([m, label]) => (
              <button role="radio" aria-checked={o().mark === m} onClick={() => p.change((x) => (x.mark = m))}>
                {label}
              </button>
            )}
          </For>
        </div>
      </div>
      <div class="view-row">
        <span class="row-label">Fade</span>
        <div class="options" role="radiogroup" aria-label="Fade units by">
          <For each={SAT_LABELS}>
            {([v, label]) => (
              <button role="radio" aria-checked={o().sat === v} onClick={() => p.change((x) => (x.sat = v))}>
                {label}
              </button>
            )}
          </For>
        </div>
      </div>
      <div class="view-row">
        <span class="row-label">Sides</span>
        <div class="chips">
          <For each={p.header.factions}>
            {(f, i) => (
              <button class="chip" aria-pressed={((o().factions >> i()) & 1) === 1} onClick={() => p.change((x) => (x.factions ^= 1 << i()))}>
                <span class="dot" style={{ background: f.color }} />
                {f.name}
              </button>
            )}
          </For>
        </div>
      </div>
      {/* Fog of war from one side's sight: fixed in a fogged live game. */}
      <Show when={p.header.fog === undefined}>
        <div class="view-row">
          <span class="row-label">Sight</span>
          <div class="options" role="radiogroup" aria-label="Fog of war from a side's sight">
            <For each={sides}>
              {([v, label]) => (
                <button role="radio" aria-checked={o().sight === v} onClick={() => p.change((x) => (x.sight = v))}>
                  {label}
                </button>
              )}
            </For>
          </div>
        </div>
      </Show>
      <div class="legend">
        <Show
          when={o().mark === "type"}
          fallback={
            <Show when={o().mark !== "none"}>
              <div class="legend-row">{item(MARK_COLOURS[o().mark as keyof typeof MARK_COLOURS], MARK_LABELS.find(([m]) => m === o().mark)?.[1] ?? "")}</div>
            </Show>
          }
        >
          <div class="legend-row">
            <For each={TYPE_TINTS}>{([, label, hue]) => item(hue, label)}</For>
          </div>
        </Show>
        <Show when={o().sat !== "none"}>
          <p class="muted small">Faded toward grey: {SAT_LOW[o().sat as keyof typeof SAT_LOW]}.</p>
        </Show>
      </div>
    </>
  );
}

export function LayersTab(p: Props): JSX.Element {
  const o = () => (p.rev(), p.options);
  // The heatmap's key: each ore's hue on its button (the atlas's), five
  // steps from little to rich (as drawn over dark ground: fainter where
  // there's less) in the chosen ore's, then ore of unknown kind.
  const GROUND: RGB = [0.12, 0.16, 0.1];
  const steps = () =>
    [0.15, 0.35, 0.55, 0.75, 1].map((k) => {
      const shown = oresOf(o().ore);
      const hue = shown.length === 1 ? oreRGB(shown[0]) : ([0.85, 0.82, 0.74] as RGB);
      return css(mixRGB(GROUND, hue, 0.9 * Math.pow(k, 0.6)));
    });
  // Key hints follow the player's bindings.
  return (
    <>
      <div class="layers">
        <For each={TOGGLES}>
          {(t) => (
            <label data-tip={t.tip}>
              <input type="checkbox" checked={(p.rev(), p.options[t.key] as boolean)} onChange={(e) => p.change((x) => ((x[t.key] as boolean) = e.currentTarget.checked))} />
              {t.label}
              {/* A key where the layer has one (a replay's; live layers have none). */}
              <Show when={keys.ACTIONS.some((x) => x.id === `layer.${t.key}`)}>
                <kbd>{(p.rev(), keys.hint(`layer.${t.key}`))}</kbd>
              </Show>
            </label>
          )}
        </For>
      </div>
      {/* The route-like layers' keys, while they're on. */}
      <div class="layer-keys">
        <For each={Object.entries(KEYS) as [keyof RenderOptions, [string, string][]][]}>
          {([key, items]) => (
            <Show when={(p.rev(), p.options[key])}>
              <div class="legend-row">
                <span class="layer-key-name">{TOGGLES.find((t) => t.key === key)?.label}</span>
                <For each={items}>
                  {([bg, what]) => (
                    <span class="legend-item">
                      <span class="swatch" style={{ background: bg }} />
                      {what}
                    </span>
                  )}
                </For>
              </div>
            </Show>
          )}
        </For>
      </div>
      <Show when={oreKnown() && o().intel}>
        <div class="ore-key">
          <div class="view-row">
            <span class="row-label">Ore</span>
            <div class="options" role="radiogroup" aria-label="The ore the heatmap shows">
              <select
                value={o().ore}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setOreLayerChoice(v);
                  p.change((x) => (x.ore = v));
                }}
                aria-label="Ores shown"
              >
                <option value="All">All ores</option>
                <For each={ORE_FAMILIES}>
                  {([family, ores]) => (
                    <optgroup label={family}>
                      <option value={`family:${family}`}>{`All ${family.toLowerCase()}`}</option>
                      <For each={ores}>{(ore) => <option value={ore}>{oreLabel(ore)}</option>}</For>
                    </optgroup>
                  )}
                </For>
              </select>
            </div>
          </div>
          <div class="legend-row">
            <span class="legend-item">
              <span class="swatches">
                <For each={steps()}>{(c) => <span class="swatch" style={{ background: c }} />}</For>
              </span>
              little to rich
            </span>
            <span class="legend-item">
              <span class="swatch" style={{ background: css(mixRGB(GROUND, ORE_UNKNOWN, 0.3)) }} />
              kind not known
            </span>
          </div>
        </div>
      </Show>
    </>
  );
}

// The ore key, mounted once (after everything it uses is defined).
const oreKeyHost = document.createElement("div");
document.body.append(oreKeyHost);
render(() => <OreKey />, oreKeyHost);
